import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {RouterLinks} from '../shared/constants';
import {UserStore} from '../authentication/state/user.store';
import {AuthenticationService} from '../authentication/service/authentication.service';
import {NavigationEnd, Router} from '@angular/router';
import {AccountService} from './services/account.service';
import {AccountSharingService} from './services/account-sharing.service';
import {Subscription} from 'rxjs';
import {PasswordChange} from './models/password-change';
import { BiometricAuth } from "@aparajita/capacitor-biometric-auth";
import { Dialog } from '@capacitor/dialog';
import { StorageService } from '../shared/services/storage.service';

declare function notify(): any;

@Component({
    selector: 'app-account',
    templateUrl: './account.component.html',
    styleUrls: ['./account.component.scss'],
    standalone: false
})
export class AccountComponent implements OnInit, OnDestroy {

  routerLinks = RouterLinks;
  activeRoute = '';
  buttonDisabled = true;
  routerSubscription: Subscription;
  passwordChangeSubscription: Subscription;
  notificationChangeSubscription: Subscription;
  passwordChange: PasswordChange;
  notificationSettingsChange: string;
  showAccountPagesSaveCloseTray = true;
  documentPageRoute: any;
  user: any;
  isBiometrics = false;
  useBiometrics: any;

  constructor(
    private userStore: UserStore,
    private authService: AuthenticationService,
    private router: Router,
    private accountService: AccountService,
    private accountSharingService: AccountSharingService,
    private cdr: ChangeDetectorRef,
    private storageService: StorageService
  ) {
    this.user = this.userStore.getValue();
    this.checkFaceID();
  }

  async checkFaceID(){
    try{
      const result = await BiometricAuth.checkBiometry();
      if (result.isAvailable) {
        this.storageService.set('biometricHardware', 'true', 'user');
        this.isBiometrics = true;
      } else {
        this.isBiometrics = this.storageService.get('biometricHardware', 'user') === 'true';
      }
      let useBiometrics = this.storageService.getJson('useBiometrics', 'user');
      this.useBiometrics = useBiometrics ?? { useBiometrics: false };
    }
    catch{}
  }
  ngOnInit(): void {
    this.user = this.userStore.getValue();
    this.documentPageRoute = this.storageService.get('previous-document-page', 'flow');
    if (!this.documentPageRoute) this.documentPageRoute = '/everything.dashboard';
    this.passwordChangeSubscription = this.accountSharingService.passwordChange
      .subscribe({
        next: value => {
          if (value?.currentPassword && value?.currentPassword) {
            this.buttonDisabled = false;
            this.passwordChange = value;
          } else {
            this.buttonDisabled = true;
          }
        }
      });
    this.notificationChangeSubscription = this.accountSharingService.notificationChange
      .subscribe({
        next: value => {
          if (value) {
            this.buttonDisabled = false;
            this.notificationSettingsChange = value;
          }
        }
      });
    this.setActiveRoute(this.router.url);
    this.routerSubscription = this.router.events.subscribe({
      next: e => {
        if (e instanceof NavigationEnd) {
          this.setActiveRoute(e?.url);
        }
      }
    });
  }

  setActiveRoute(currentUrl: string): void {
    if (currentUrl?.includes(RouterLinks.ChangePassword)) {
      this.activeRoute = RouterLinks.ChangePassword;
      this.buttonDisabled = true;
      this.showAccountPagesSaveCloseTray = true;
    } else if (currentUrl?.includes(RouterLinks.PaymentDetails)) {
      this.activeRoute = RouterLinks.PaymentDetails;
      this.buttonDisabled = true;
      this.showAccountPagesSaveCloseTray = false;
      setTimeout(() => {
        document.querySelector('body')?.classList.remove('page--central-tray');
      }, 500);
    } else if (currentUrl?.includes(RouterLinks.NotificationSettings)) {
      this.activeRoute = RouterLinks.NotificationSettings;
      this.buttonDisabled = false;
      this.showAccountPagesSaveCloseTray = true;
    } else if (currentUrl?.includes(RouterLinks.Referrals)) {
      this.activeRoute = RouterLinks.Referrals;
      
      this.showAccountPagesSaveCloseTray = false;
    } else if (currentUrl?.includes(RouterLinks.Wallet)) {
      this.activeRoute = RouterLinks.Wallet;
      this.showAccountPagesSaveCloseTray = false;
    }
    
  }

  save(): void {
    if (this.activeRoute === RouterLinks.ChangePassword) {
      this.accountService.changePassword(this.passwordChange).subscribe({
        next: (c: any[]) => {
          if (c?.length && c[0]?.response_code.toString() === '200') {
            this.updateNotification('Your password has been changed successfully!');
          }
        }
      });
    } else if (this.activeRoute === RouterLinks.NotificationSettings) {
      if (this.notificationSettingsChange) {
        this.accountService.updateNotificationSettings(this.notificationSettingsChange)
          .subscribe({
            next: () => {
              this.notificationSettingsChange = '';
              this.updateNotification('Your notification settings have been changed successfully!');
            }
          });
      } else {
        this.router.navigate(['/' + RouterLinks.DashboardLanding]);
        this.updateNotification('There were no changes to submit', 'info');
      }
    }
  }

  updateNotification(text: string, iconSign = 'complete'): void {
    notify()?.init({
      message: text, // required
      icon: iconSign,   // default: null; options: 'info', 'complete', or any icon-name really
      size: 'lg',     // default: null; options: 'lg'
      style: 'info',  // default: null; options: 'info', 'success',
      close: true,    // default: false (close button enabled, auto-close duration removed)
      speed: 350,     // default: 350 (fade in/out speed)
      duration: 5000  // default: 5000 (duration active)
    });
  }

  logout(): void {
    this.userStore.reset();
    this.authService.stopRefreshTokenTimer();
    this.storageService.remove('rememberMe', 'user');
    this.storageService.remove('partner_code', 'flow');
    this.router.navigate(['/login']);
  }
  async setBiometrics(){
    try {
      await BiometricAuth.authenticate({
        reason: "For easy log in",
        androidTitle: "Log in",
      });
      this.useBiometrics.useBiometrics = !this.useBiometrics.useBiometrics;
      this.storageService.setJson('useBiometrics', this.useBiometrics, 'user');
    } catch {
      if (!this.useBiometrics?.useBiometrics) {
        const { value } = await Dialog.confirm({
          title: 'Enable Face ID',
          message: 'To use Face ID for Everything, you need to allow access in iOS Settings.',
          okButtonTitle: 'Open Settings',
          cancelButtonTitle: 'Cancel'
        });
        if (value) {
          window.open('app-settings:', '_system');
        }
      }
      const checkbox = document.getElementById('faceid') as HTMLInputElement;
      if (checkbox) checkbox.checked = this.useBiometrics?.useBiometrics ?? false;
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.passwordChangeSubscription?.unsubscribe();
  }

}
